import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Paperclip, 
  Trash2, 
  Plus,
  Loader2,
  Mail,
  User,
  Calendar,
  Briefcase,
  Phone,
  MapPin,
  Copy,
  Check,
  Eye,
  Code,
  FileCode
} from 'lucide-react';
import { toast } from 'react-toastify';
import SummernoteEditor from '../SummernoteEditor';
import '../styles/EmailTemplateForm.css';
import juice from 'juice';

import {
  createTemplate,
  updateTemplate,
  EmailTemplate,
  EmailTemplateFormData
} from '../../services/emailTemplate_service';

interface Props {
  template?: EmailTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EmailTemplateForm({ template, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [showAcceptedFormats, setShowAcceptedFormats] = useState(false);
  const [importedHtml, setImportedHtml] = useState<{
    file?: File;
    content?: string;
    name?: string;
  }>({});

  const [formData, setFormData] = useState<EmailTemplateFormData>({
    name: '',
    subject: '',
    content: '',
    description: '',
    category: 'custom',
    attachments: []
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState(template?.attachments || []);

  const htmlInputRef = useRef<HTMLInputElement | null>(null);

  const acceptedFileTypes = [
    '.pdf', '.doc', '.docx', '.txt', '.rtf',
    '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg',
    '.xlsx', '.xls', '.csv',
    '.ppt', '.pptx',
    '.md', '.json', '.xml'
  ];

  const maxFileSize = 10 * 1024 * 1024;

  const availableVariables = [
    { label: 'Prénom',    value: '{{prenom}}',    icon: User     },
    { label: 'Nom',       value: '{{nom}}',       icon: User     },
    { label: 'Email',     value: '{{email}}',     icon: Mail     },
    { label: 'Téléphone', value: '{{telephone}}', icon: Phone    },
    { label: 'Adresse',   value: '{{adresse}}',   icon: MapPin   },
    { label: 'Société',   value: '{{company}}',   icon: Briefcase},
    { label: 'Date',      value: '{{date}}',      icon: Calendar },
  ];

  const inlineStyles = (htmlContent: string): string => {
    if (!htmlContent) return '';
    try {
      return juice(htmlContent, {
        inlinePseudoElements: true,
        preserveImportant: true,
        removeStyleTags: true,
        preserveMediaQueries: true,
        applyWidthAttributes: true,
        applyHeightAttributes: true,
        applyAttributesTableElements: true
      });
    } catch {
      return htmlContent;
    }
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        content: template.content,
        description: template.description || '',
        category: template.category,
        attachments: []
      });
      setExistingAttachments(template.attachments || []);
      if (template.content) {
        setImportedHtml({ content: template.content, name: 'Template existant' });
      }
    }
  }, [template]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSummernoteChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (file.size > maxFileSize)
      return { valid: false, error: `${file.name} dépasse la limite de 10 MB` };
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedFileTypes.includes(ext))
      return { valid: false, error: `Format ${ext} non accepté` };
    return { valid: true };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    const validFiles: File[] = [];
    for (const file of files) {
      const v = validateFile(file);
      if (v.valid) validFiles.push(file);
      else toast.error(v.error);
    }
    const totalSize = [...selectedFiles, ...validFiles].reduce((s, f) => s + f.size, 0);
    if (totalSize > maxFileSize) {
      toast.error('Taille totale des pièces jointes > 10 MB');
      return;
    }
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleHtmlImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'html' && ext !== 'htm') {
      toast.error('Veuillez sélectionner un fichier HTML valide');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) {
        const inlined = inlineStyles(ev.target.result as string);
        setFormData(prev => ({ ...prev, content: inlined }));
        setImportedHtml({ file, content: inlined, name: file.name });
        toast.success('Contenu HTML importé et styles inlinés avec succès');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleRemoveHtml = () => {
    setFormData(prev => ({ ...prev, content: '' }));
    setImportedHtml({});
    if (htmlInputRef.current) htmlInputRef.current.value = '';
    toast.info('Fichier HTML supprimé');
  };

  const removeSelectedFile = (index: number) =>
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));

  const removeExistingAttachment = (id: number) =>
    setExistingAttachments(prev => prev.filter(a => a.id !== id));

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
    setCopiedVariable(variable);
    setTimeout(() => setCopiedVariable(null), 2000);
    toast.info(`Variable ${variable} copiée !`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim())    { toast.error('Le nom du template est obligatoire'); return; }
    if (!formData.subject.trim()) { toast.error("L'objet est obligatoire"); return; }
    if (!formData.content.trim()) { toast.error('Le contenu est obligatoire'); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('subject', formData.subject);
      fd.append('content', inlineStyles(formData.content));
      if (formData.description) fd.append('description', formData.description);
      if (formData.category)    fd.append('category', formData.category);
      selectedFiles.forEach(f => fd.append('attachments', f));

      if (template) {
        await updateTemplate(template.id, fd);
        toast.success('Template mis à jour avec succès');
      } else {
        await createTemplate(fd);
        toast.success('Template créé avec succès');
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const getPreviewContent = () =>
    formData.content ? inlineStyles(formData.content) : '<p style="color:#aaa">Aucun contenu à prévisualiser</p>';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>

        {/* ── Header ────────────────────────────────── */}
        <div className="modal-header">
          <div className="modal-header-left">
            <div className="modal-icon">
              <Mail size={20} />
            </div>
            <div>
              <h2 className="modal-title">
                {template ? 'Modifier le template' : 'Nouveau template'}
              </h2>
              <p className="modal-subtitle">
                {template
                  ? 'Modifiez les informations du template email'
                  : "Créez un nouveau template d'email"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="modal-close" title="Fermer">
            <X size={16} />
          </button>
        </div>

        {/* ── Form ──────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="modal-form">

          {/* Section : Informations générales */}
          <div className="form-section">
            <h3 className="form-section-title">Informations générales</h3>

            <div className="form-grid" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label className="form-label">
                  Nom du template <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="ex : Bienvenue nouveau client"
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Objet de l'email <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="ex : Bienvenue {{prenom}} chez nous !"
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Section : Variables dynamiques */}
          <div className="form-section">
            <h3 className="form-section-title">Variables dynamiques</h3>
            <p className="form-section-description">
              Cliquez pour copier une variable et la coller dans votre contenu
            </p>
            <div className="variables-grid">
              {availableVariables.map(({ label, value, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => copyVariable(value)}
                  className={`variable-chip ${copiedVariable === value ? 'copied' : ''}`}
                >
                  <Icon size={13} />
                  <span>{label}</span>
                  {copiedVariable === value
                    ? <Check size={13} className="check-icon" />
                    : <Copy  size={13} className="copy-icon"  />}
                </button>
              ))}
            </div>
          </div>

          {/* Section : Contenu */}
          <div className="form-section">
            <div className="content-section-header">
              <h3 className="form-section-title" style={{ margin: 0 }}>
                Contenu de l'email <span className="required">*</span>
              </h3>
              <div className="content-section-actions">
                {/* Import HTML — secondary action */}
                <button
                  type="button"
                  onClick={() => htmlInputRef.current?.click()}
                  className="btn-secondary"
                >
                  <FileCode size={14} />
                  Importer HTML
                </button>
                {/* Preview toggle — secondary action */}
                <button
                  type="button"
                  onClick={() => setShowHtmlPreview(true)}
                  className="btn-secondary"
                  style={{ display: showHtmlPreview ? 'none' : undefined }}
                >
                  <Eye size={14} />
                  Aperçu
                </button>
              </div>
            </div>

            {/* Hidden HTML file input */}
            <input
              type="file"
              accept=".html,.htm"
              style={{ display: 'none' }}
              ref={htmlInputRef}
              onChange={handleHtmlImport}
            />

            {/* Imported HTML indicator */}
            {importedHtml.name && (
              <div className="imported-html-indicator">
                <FileCode size={15} style={{ color: 'var(--teal-dark)', flexShrink: 0 }} />
                <span className="imported-html-name">{importedHtml.name}</span>
                <span className="imported-html-badge">styles inlinés ✓</span>
                <button
                  type="button"
                  className="attachment-remove"
                  onClick={handleRemoveHtml}
                  title="Supprimer le fichier HTML"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}

            {/* Editor or Preview — strictly mutually exclusive */}
            {showHtmlPreview ? (
              <div className="html-preview">
                <div className="html-preview-header">
                  <span>Aperçu du rendu (styles inlinés)</span>
                  <button
                    type="button"
                    className="btn-icon-small"
                    onClick={(e) => { e.stopPropagation(); setShowHtmlPreview(false); }}
                    title="Fermer l'aperçu"
                  >
                    <X size={13} />
                  </button>
                </div>
                <div
                  className="html-preview-content"
                  dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                />
              </div>
            ) : (
              <div className="editor-container" key="summernote-editor">
                <SummernoteEditor
                  value={formData.content}
                  onChange={handleSummernoteChange}
                />
              </div>
            )}
          </div>

          {/* Section : Pièces jointes */}
          <div className="form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 className="form-section-title" style={{ margin: 0 }}>Pièces jointes</h3>
              <button
                type="button"
                className="btn-link"
                onClick={() => setShowAcceptedFormats(v => !v)}
              >
                {showAcceptedFormats ? 'Masquer' : 'Formats acceptés'}
              </button>
            </div>

            {showAcceptedFormats && (
              <div className="accepted-formats">
                <p className="formats-title">Formats acceptés</p>
                <div className="formats-grid">
                  {['PDF','DOC/DOCX','Images','XLS/XLSX','PPT/PPTX','TXT/RTF','CSV','XML/JSON','MD'].map(f => (
                    <span key={f} className="format-badge">{f}</span>
                  ))}
                </div>
                <p className="upload-hint" style={{ marginTop: '0.5rem' }}>
                  ⓘ Les fichiers HTML s'importent via le bouton « Importer HTML » ci-dessus
                </p>
              </div>
            )}

            {/* Existing attachments */}
            {existingAttachments.length > 0 && (
              <div className="attachments-list">
                <label className="form-label">Fichiers existants</label>
                {existingAttachments.map(att => (
                  <div key={att.id} className="attachment-item">
                    <div className="attachment-info">
                      <Paperclip size={13} />
                      <span className="attachment-name">{att.file_name}</span>
                      <span className="attachment-size">({(att.file_size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => removeExistingAttachment(att.id)}
                      title="Supprimer"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* New files */}
            {selectedFiles.length > 0 && (
              <div className="attachments-list">
                <label className="form-label">Nouveaux fichiers</label>
                {selectedFiles.map((file, i) => (
                  <div key={i} className="attachment-item">
                    <div className="attachment-info">
                      <Paperclip size={13} />
                      <span className="attachment-name">{file.name}</span>
                      <span className="attachment-size">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => removeSelectedFile(i)}
                      title="Supprimer"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="upload-button">
              <Plus size={15} />
              <span>Ajouter des fichiers</span>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept={acceptedFileTypes.join(',')}
              />
            </label>
            <p className="upload-hint">Tous formats acceptés sauf HTML — max 10 MB</p>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="spinner" />
                  {template ? 'Mise à jour…' : 'Création…'}
                </>
              ) : (
                template ? 'Mettre à jour' : 'Créer le template'
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}